// De verwijzings-renderer: [[trend:uuid|titel]] wordt een klikbare verwijzing.
//
// EEN GEDEELDE RENDERER, EN DAT IS EEN GEMETEN LES. Twee keer eerder liep dit
// mis in dit project: de {n}-bronmarkers stonden letterlijk op het scherm bij
// ruim duizend trends (sessie 1), en de [[trend:…]]-markers werden door de
// chatpopup rauw getoond omdat niemand ze renderde (chat-iteratie 2). Beide
// keren was de oorzaak dezelfde: de backend produceerde een markering waar het
// scherm geen weet van had. Vandaar één renderer, in een eigen bestand, die de
// redactiekaart en de chat allebei kunnen gebruiken.
//
// De regel bij een marker die niet opgelost kan worden is dezelfde als bij de
// {n}-markers in TrendDetail: liever NIETS dan een verkeerde verwijzing. Een
// verwijzing naar een trend die niet meer bestaat wordt gewone tekst — de
// titel die in de marker staat blijft leesbaar, alleen de klik verdwijnt.

import type { ReactNode } from "react";

import { color, weight } from "../tokens";

/**
 * [[soort:id|titel]] — soort is trend | skill | bron.
 *
 * De bron staat als string zodat een andere renderer erop kan splitsen zonder
 * het patroon over te typen: de chat moet binnen één regel eerst de markers
 * afzonderen en pas daarna de getallen van een geteld-badge voorzien (een uuid
 * bevat cijfers, dus andersom zou hij een marker doormidden knippen). Twee
 * kopieën van dit patroon is precies hoe markerfouten ontstaan.
 */
export const MARKER_BRON = String.raw`\[\[(trend|skill|bron):([0-9a-fA-F-]{8,}?)\|([^\]]+)\]\]`;

const MARKER = new RegExp(MARKER_BRON, "g");

export type VerwijzingSoort = "trend" | "skill" | "bron";

export type VerwijzingKlik = (soort: VerwijzingSoort, id: string) => void;

function Chip({
  titel,
  onClick,
}: {
  titel: string;
  onClick?: () => void;
}) {
  if (!onClick) return <>{titel}</>;
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        borderBottom: `1px solid ${color.disabled}`,
        padding: 0,
        margin: 0,
        font: "inherit",
        fontWeight: weight.medium,
        color: color.ink,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderBottomColor = color.ink;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderBottomColor = color.disabled;
      }}
    >
      {titel}
    </button>
  );
}

/**
 * Rendert tekst met verwijzingen. Zonder `onOpen` blijft alles leesbare tekst:
 * dat is de stand op een scherm dat nergens heen kan navigeren, en nog steeds
 * beter dan een rauwe marker.
 */
export function Verwijzingen({
  tekst,
  onOpen,
}: {
  tekst: string;
  onOpen?: VerwijzingKlik;
}) {
  if (!tekst) return null;
  const stukken: ReactNode[] = [];
  let laatste = 0;
  let match: RegExpExecArray | null;
  MARKER.lastIndex = 0;
  while ((match = MARKER.exec(tekst)) !== null) {
    if (match.index > laatste) stukken.push(tekst.slice(laatste, match.index));
    const [, soort, id, titel] = match;
    stukken.push(
      <Chip
        key={`${id}-${match.index}`}
        titel={titel!}
        onClick={onOpen ? () => onOpen(soort as VerwijzingSoort, id!) : undefined}
      />,
    );
    laatste = match.index + match[0].length;
  }
  if (laatste === 0) return <>{tekst}</>;
  if (laatste < tekst.length) stukken.push(tekst.slice(laatste));
  return <>{stukken}</>;
}
